import { computed, Directive, inject, input, output, viewChild } from "@angular/core";

import { Observable } from "rxjs";
import { plainToInstance } from "class-transformer";

import { AuthService } from "@core/services/auth.service";
import { LoggerService } from "@core/services/logger.service";
import { TreeNode } from "@documentation/models/folder.model";
import { ModalService } from "@shared/components/modal/modal.service";
import { ToggleComponent } from "@shared/components/toggle/toggle.component";
import { ToastService } from "@shared/services/toast.service";
import { Constructor } from "@shared/types/common-types.model";

@Directive()
export abstract class BaseDocumentListContextMenuComponent<T extends TreeNode> {
  readonly entity = input.required<T>();
  readonly isInPublicFolder = input.required<boolean>();

  readonly entityUpdated = output<T>();
  readonly entityMoved = output<T>();
  readonly treeUpdated = output<void>();

  protected readonly _auth = inject(AuthService);
  protected readonly _modalService = inject(ModalService);
  protected readonly _toastService = inject(ToastService);
  protected readonly _logger = inject(LoggerService);

  protected readonly isTemporaryUser = computed(() => !!this._auth.currentUser()?.isTemporary);
  protected readonly isOwner = computed(() => this.isOwnerComputation());

  protected readonly publicStatusToggle = viewChild(ToggleComponent);

  protected abstract readonly entityType: Constructor<T>;

  // Messages for toast notifications
  protected abstract readonly publicSuccessMessage: string; // message when entity becomes public
  protected abstract readonly privateSuccessMessage: string; // message when entity becomes private

  protected onMenuClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  protected handleOnRenameClick(event: Event, currentName: string, title: string, label: string): void {
    event.preventDefault();
    event.stopPropagation();

    this._modalService.openRenameEntityModal(currentName, title, label).result.then((result) => {
      if (result) {
        this.handleRename(result);
      }
    });
  }

  protected async onTogglePublic(isPublic: boolean): Promise<void> {
    try {
      const currentStatus = this.entity().isPublic!;
      const request$ = await this.togglePublicRequest(isPublic);

      request$.subscribe({
        next: () => {
          // Emit updated entity so parent can update its list
          this.entityUpdated.emit(this.getUpdatedModel({ isPublic }));
          this._toastService.success(isPublic ? this.publicSuccessMessage : this.privateSuccessMessage);
          this.handlePublicStatusChange();
        },
        error: () => {
          this._logger.error("Failed to toggle public status");
          this._toastService.error("Failed to update visibility");
          this.publicStatusToggle()?.writeValue(currentStatus);
        }
      });
    } catch (e) {
      this._logger.error("Failed to toggle public", e);
      this._toastService.error("Failed to update visibility");
    }
  }

  protected onCopyPublicLink(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const doc = this.entity();
    if (!doc || !doc.isPublic) return;

    try {
      const origin = window.location.origin;
      const publicDocumentUrl = this.getPublicEntityUrl(doc.id);
      const url = `${origin}${publicDocumentUrl}`;
      navigator.clipboard.writeText(url).then(
        () => this._toastService.success("Public link copied"),
        () => this._toastService.error("Failed to copy link")
      );
    } catch (e) {
      this._logger.error("Failed to copy public link", e);
      this._toastService.error("Failed to copy link");
    }
  }

  private isOwnerComputation(): boolean {
    const item = this.entity();

    if (!item) return false;
    const me = this._auth.currentUser();
    return !!me && item.creatorId === me.id;
  }

  protected getUpdatedModel(updates: Partial<TreeNode>): T {
    return plainToInstance(this.entityType, { ...this.entity(), ...updates }, { excludeExtraneousValues: true });
  }

  protected handlePublicStatusChange(): void {}

  protected abstract getPublicEntityUrl(id: string): string;

  protected abstract handleRename(newName: string): void;

  protected abstract togglePublicRequest(isPublic: boolean): Promise<Observable<unknown>>;
}
